import { useCallback, useMemo } from 'react';
import { useRouter as useAppRouterNext } from 'next/router';

import { useConfirmUnsavedNavigation } from './useConfirmUnsavedNavigation';

type RouterPush = ReturnType<typeof useAppRouterNext>['push'];

/**
 * Fix reloads
 * https://stackoverflow.com/questions/61130038/next-js-router-push-reloads-page-automatically
 * https://stackoverflow.com/questions/61948237/next-js-shallow-routing-with-dynamic-routes
 */
/**
 * Issues
 * Remove shallow https://ownerone.atlassian.net/browse/WA-2669
 */
export const useAppRouter = () => {
  const routerNext = useAppRouterNext();
  const confirmUnsavedNavigation = useConfirmUnsavedNavigation();

  const confirmAndNavigate = useCallback(
    async (navigate: () => ReturnType<RouterPush>) => {
      const canNavigate = await confirmUnsavedNavigation();
      if (!canNavigate) return false;

      return navigate();
    },
    [confirmUnsavedNavigation],
  );

  const pushShallow = useCallback<(typeof routerNext)['push']>(
    (url, as) => {
      return routerNext.push(url, as, { shallow: true });
    },
    [routerNext],
  );

  const replaceShallow = useCallback<(typeof routerNext)['replace']>(
    (url, as) => {
      return routerNext.replace(url, as, { shallow: true });
    },
    [routerNext],
  );

  const pushShallowGuarded = useCallback<(typeof routerNext)['push']>(
    (url, as) => {
      return confirmAndNavigate(() => routerNext.push(url, as, { shallow: true }));
    },
    [confirmAndNavigate, routerNext],
  );

  const pushGuarded = useCallback<(typeof routerNext)['push']>(
    (url, as, options) => {
      return confirmAndNavigate(() => routerNext.push(url, as, options));
    },
    [confirmAndNavigate, routerNext],
  );

  const replaceShallowGuarded = useCallback<(typeof routerNext)['replace']>(
    (url, as) => {
      return confirmAndNavigate(() => routerNext.replace(url, as, { shallow: true }));
    },
    [routerNext],
  );

  return useMemo(() => {
    return {
      ...routerNext,
      pushGuarded,
      pushShallow,
      pushShallowGuarded,
      replaceShallow,
      replaceShallowGuarded,
    };
  }, [
    routerNext,
    pushGuarded,
    pushShallow,
    pushShallowGuarded,
    replaceShallow,
    replaceShallowGuarded,
  ]);
};
